/**
 * Device stability — which devices keep dropping off (EPIC B8).
 *
 * A flapping device is rarely a device problem on its own: it is usually a
 * failing NIC, a dying switch port, marginal PoE, a weak Wi-Fi edge or a power
 * issue. It is also one of the biggest producers of alert noise, which is why
 * this sits next to the digest work — the digest quiets the symptom, this names
 * the cause.
 *
 * The page's main job beyond listing devices is to be HONEST about coverage.
 * Transitions are only recorded from the moment the feature shipped, so an empty
 * list on a fresh install must not read as "your fleet is stable". Hence the
 * history banner, driven by history_hours from the API.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import api from "../api/client";
import { blobErrorDetail, downloadAuthed } from "../utils/download";
import ActionButton from "../components/ActionButton";
import { useCustomer } from "../context/CustomerContext";

type Row = {
  // null on a network device — see `kind`.
  device_id: number | null;
  network_device_id: number | null;
  kind: "endpoint" | "network";
  device_subtype: string | null;
  // Resolved server-side: network devices have no per-device route, so a
  // /devices/{id} link assembled here from their id would open an unrelated
  // machine, or nothing.
  detail_path: string;
  hostname: string;
  customer_id: number | null;
  customer_name: string | null;
  current_status: string | null;
  transitions: number;
  drops: number;
  median_offline_seconds: number | null;
  median_offline_human: string;
  total_offline_human: string;
  assessment: string;
};

type Report = {
  window_hours: number;
  min_transitions: number;
  history_since: string | null;
  history_hours: number;
  /** Coverage per population. Network gear has far less than endpoints do:
   *  the transitions recorded before the relay's collector-election fix were
   *  purged because a device's state was being overwritten by whichever
   *  agent wrote last. One combined number would say "598 hours" while every
   *  switch had three days, making an empty row read as proven-stable. */
  network_history_hours: number;
  endpoint_history_hours: number;
  devices: Row[];
  device_count: number;
};

const WINDOWS = [
  { hours: 24, label: "Last 24 hours" },
  { hours: 24 * 7, label: "Last 7 days" },
  { hours: 24 * 30, label: "Last 30 days" },
];

/** How many drops before we call it flapping. Lower = noisier, higher = only
 *  the worst. Exposed because "flapping" means different things on a stable
 *  office LAN and a fleet of field laptops. */
const SENSITIVITY = [
  { min: 4, label: "Sensitive (2+ drops)" },
  { min: 6, label: "Normal (3+ drops)" },
  { min: 12, label: "Only the worst (6+ drops)" },
];

export default function DeviceStability() {
  const { currentCustomerId } = useCustomer();
  const [windowHours, setWindowHours] = useState(24);
  const [minTransitions, setMinTransitions] = useState(6);
  const [report, setReport] = useState<Report | null>(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const params: Record<string, unknown> = {
        window_hours: windowHours,
        min_transitions: minTransitions,
      };
      if (currentCustomerId) params.customer_id = currentCustomerId;
      const r = await api.get("/api/v1/reports/flapping", { params });
      setReport(r.data);
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message || "Could not load the report");
    } finally {
      setLoading(false);
    }
  }, [windowHours, minTransitions, currentCustomerId]);

  const exportAs = async (fmt: "pdf" | "csv") => {
    const qs = new URLSearchParams({
      fmt,
      window_hours: String(windowHours),
      min_transitions: String(minTransitions),
      ...(currentCustomerId ? { customer_id: String(currentCustomerId) } : {}),
    });
    try {
      await downloadAuthed(
        `/api/v1/reports/flapping/export?${qs.toString()}`,
        `device-stability.${fmt}`,
        fmt === "pdf" ? "application/pdf" : "text/csv",
      );
    } catch (e) {
      setErr(await blobErrorDetail(e, "Could not build the export."));
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  // Recording only began when this feature shipped, so a short history makes an
  // empty result meaningless rather than reassuring.
  const thin = !!report && report.history_hours < windowHours;
  // Network gear is thin long after endpoints stop being thin, so it needs
  // saying separately - otherwise the absence of a banner implies coverage
  // the switches do not have.
  //
  // NOT suppressed when the general banner is showing, which was the first
  // attempt and was worse than nothing: on a 30-day window the general
  // banner says "600 hours of history", and a reader applies that to the
  // switches too, when they have 76. The two statements are about different
  // populations, so both belong on screen when both are true.
  const netThin =
    !!report &&
    report.network_history_hours < windowHours &&
    report.network_history_hours < report.endpoint_history_hours;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Device stability
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Devices that keep dropping off and coming back. Repeated short drops are
          usually a link, switch-port or PoE fault rather than a problem with the
          machine itself — and they are one of the largest sources of alert noise.
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-sm text-gray-600 dark:text-gray-300">Period</label>
          <select
            value={windowHours}
            onChange={(e) => setWindowHours(Number(e.target.value))}
            className="border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm"
          >
            {WINDOWS.map((w) => (
              <option key={w.hours} value={w.hours}>
                {w.label}
              </option>
            ))}
          </select>

          <label className="text-sm text-gray-600 dark:text-gray-300 ml-2">
            Threshold
          </label>
          <select
            value={minTransitions}
            onChange={(e) => setMinTransitions(Number(e.target.value))}
            className="border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm"
          >
            {SENSITIVITY.map((s) => (
              <option key={s.min} value={s.min}>
                {s.label}
              </option>
            ))}
          </select>

          <ActionButton onAction={load} pendingLabel="Loading…" size="sm">
            Refresh
          </ActionButton>

          {/* Export what is ON SCREEN.
            *
            * REPORTED: "Device Stability - need to be able to export the
            * results as a report." The same window, threshold and customer
            * go to the server, so the document matches the table rather
            * than being a second, subtly different query.
            *
            * downloadAuthed, not a plain <a href>: the API needs the bearer
            * token and a bare link returns {"detail":"Not authenticated"}. */}
          <ActionButton onAction={() => exportAs("pdf")} pendingLabel="Building…" size="sm">
            Export PDF
          </ActionButton>
          <ActionButton onAction={() => exportAs("csv")} pendingLabel="Building…" size="sm">
            Export CSV
          </ActionButton>

          <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
            {currentCustomerId
              ? "Showing the selected customer"
              : "Showing all customers"}
          </span>
        </div>

        {thin && (
          <p className="mt-3 rounded-lg bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
            Only <strong>{report!.history_hours} hours</strong> of state history has
            been recorded so far, which is less than the period selected. Device
            transitions are recorded from the moment this feature was enabled — there
            was no history to backfill — so an empty result here means "not enough
            data yet", not "nothing is flapping".
          </p>
        )}

        {netThin && (
          <p className="mt-3 rounded-lg bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
            Endpoints have <strong>{report!.endpoint_history_hours} hours</strong> of
            history, but switches, routers and access points have only{" "}
            <strong>{report!.network_history_hours} hours</strong>. Everything recorded
            for network gear before 18 Sep 2026 was removed: a monitoring fault meant
            each device's state was being overwritten by whichever on-site agent
            reported last, so devices appeared to drop in lockstep when they had not.
            A network device with no rows here means "not measured for long enough",
            not "stable".
          </p>
        )}
      </div>

      {err && (
        <div className="bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 px-3 py-2 rounded text-sm">
          {err}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
        {loading ? (
          <p className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
            Loading…
          </p>
        ) : !report || report.device_count === 0 ? (
          <div className="p-8 text-center">
            <p className="text-sm text-gray-700 dark:text-gray-200">
              No devices crossed the flapping threshold in this period.
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {thin
                ? "Check back once more history has accumulated."
                : "Lower the threshold if you want to see milder instability."}
            </p>
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-4 py-2 text-left">Device</th>
                <th className="px-4 py-2 text-left">Customer</th>
                <th className="px-4 py-2 text-right">Drops</th>
                <th className="px-4 py-2 text-right">Typical outage</th>
                <th className="px-4 py-2 text-right">Total down</th>
                <th className="px-4 py-2 text-left">Likely cause</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
              {report.devices.map((r) => (
                <tr
                  key={`${r.kind}-${r.device_id ?? r.network_device_id}`}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/30"
                >
                  <td className="px-4 py-2">
                    <Link
                      to={r.detail_path}
                      className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                    >
                      {r.hostname}
                    </Link>
                    {/* Which kind of hardware. A switch flapping and a laptop
                        flapping are different conversations, and until now the
                        report could not show the switch at all. */}
                    {r.kind === "network" && (
                      <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                        {r.device_subtype || "network"}
                      </span>
                    )}
                    {r.current_status && (
                      <span
                        className={`ml-2 text-[11px] ${
                          r.current_status === "online"
                            ? "text-emerald-600 dark:text-emerald-400"
                            : "text-gray-400"
                        }`}
                      >
                        {r.current_status}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-gray-600 dark:text-gray-300">
                    {r.customer_name || "—"}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums font-medium">
                    {r.drops}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-600 dark:text-gray-300">
                    {r.median_offline_human}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-600 dark:text-gray-300">
                    {r.total_offline_human}
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-600 dark:text-gray-300 max-w-md">
                    {r.assessment}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
